import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const { profile } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMemberships = useCallback(async () => {
    if (!profile) {
      setMemberships([]);
      setCurrentOrg(null);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('organization_members')
      .select('*, organization:organizations(*)')
      .eq('user_id', profile.id)
      .is('deleted_at', null);

    if (error) {
      console.error('Failed to fetch memberships:', error);
      setLoading(false);
      return;
    }

    setMemberships(data || []);

    // Restore last org from localStorage or pick first
    const savedOrgId = localStorage.getItem('pi-portal-current-org');
    const saved = data?.find(m => m.org_id === savedOrgId);
    const selected = saved || data?.[0] || null;
    setCurrentOrg(selected?.organization || null);
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const switchOrg = useCallback((orgId) => {
    const membership = memberships.find(m => m.org_id === orgId);
    if (membership) {
      setCurrentOrg(membership.organization);
      localStorage.setItem('pi-portal-current-org', orgId);
    }
  }, [memberships]);

  const currentMembership = memberships.find(m => m.org_id === currentOrg?.id);
  const currentRole = currentMembership?.role || null;

  const value = {
    memberships,
    currentOrg,
    currentRole,
    currentMembership,
    loading,
    switchOrg,
    refetchMemberships: fetchMemberships,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
